import { IUser, ServiceStatusEnum } from "@imaginelearning/mps-api-service";
import {
  classMap,
  UxAvatar,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxAvatarStyleEnum,
  UxBackground,
  UxBackgroundEnum,
  UxIcon,
  UxIconEnum,
  UxNavbar,
  UxOrientationEnum,
  UxPositionEnum,
  UxPriorityEnum,
  UxSizeExtendedEnum
} from "@imaginelearning/mps-ux-component-library";
import React, { createContext, Suspense, useContext, useMemo, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import useGradeRange from "hooks/usegraderange";
import { UserContext } from "../../context-providers/user.provider";
import UserRewardStars from "../user-reward-stars/user-reward-stars";
import EdgeUserProfile from "../edge-user-profile/edge-user-profile";
import AssignmentCourseMap from "./assignment-course-map";
import MyAssignmentsMenu from "./my-assignment-page/my-assignment-menu";
import "./my-assignments-container.scss";
import useTheme from "../../hooks/usetheme";

const defaultDispatchString: React.Dispatch<React.SetStateAction<string>> = (val) => val;
const defaultDispatchInteger: React.Dispatch<React.SetStateAction<number>> = (val) => val;

const defaultState = {
  ilpID: "",
  setIlpId: defaultDispatchString,
  contentType: -1,
  setContentType: defaultDispatchInteger
};
function MyAssignmentsContainer() {
  const { userState } = useContext(UserContext);
  const user: IUser = userState.response as IUser;
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isKto8, is9to12 } = useGradeRange();
  const spinner = <UxIcon icon={UxIconEnum.CIRCLE_NOTCH} />;
  const pathName = useLocation().pathname;
  const [ilpID, setIlpId] = useState("");
  const [contentType, setContentType] = useState(-1);
  const rewards = pathName === "/rewards";
  const progress = pathName.includes("/myprogress");

  const baseClasses = "d-flex text-xxl text-white me-2";
  const handleNavigation = (path: string, state?: {}) => navigate(path, { state });
  const lessonsLogo = {
    type: "custom" as const,
    icon: UxIconEnum.MY_PATH_LOGO,
    title: "My Assignments",
    size: UxSizeExtendedEnum.X4,
    className: classMap(baseClasses, "fw-normal filters brightness-10"),
    logoClassName: "text-white"
  };

  const alpContextValue = useMemo(
    () => ({ ilpID, setIlpId, contentType, setContentType }),
    [ilpID, setIlpId, contentType, setContentType]
  );

  const children = [
    {
      item: isKto8 ? <UserRewardStars onStarBarClick={() => handleNavigation("/rewards")} /> : <div />
    },
    {
      item: userState.status === ServiceStatusEnum.LOADED && user.selectedAvatar && (
        <UxAvatar
          avatar={user.selectedAvatar as UxAvatarEnum}
          avatarType={UxAvatarPriorityEnum.DEFAULT}
          size={UxSizeExtendedEnum.X1}
          avatarStyle={UxAvatarStyleEnum.BODY}
          className={`active-avatar d-flex ${is9to12 ? "with-border" : ""}`}
          theme={theme}
        />
      )
    },
    {
      item: (
        <EdgeUserProfile
          userFirstname={user?.firstname || ""}
          avatar={user.selectedAvatar as UxAvatarEnum}
          handleNavigation={handleNavigation}
        />
      )
    }
  ];
  return (
    <div className={classMap("my-assignments-container w-100 h-100", String(theme).replace(/_/g, "-"))}>
      <UxBackground
        background={user.selectedBackground ? (user.selectedBackground as UxBackgroundEnum) : UxBackgroundEnum.DEFAULT}
        className="position-fixed z-index--1"
        theme={theme}
      />
      <UxNavbar
        color={UxPriorityEnum.NAVBAR}
        orientation={UxOrientationEnum.HORIZONTAL}
        items={children}
        brand={lessonsLogo}
        className={`text-lg fw-bold home-navbar ${rewards || progress ? "rewards-navbar" : ""}`}
        bgClassName="opacity opacity-80"
        fixed={UxPositionEnum.TOP}
        role="menubar"
      />
      {userState.status === ServiceStatusEnum.LOADED && (
        <AlpContext.Provider value={alpContextValue}>
          <Suspense fallback={spinner}>
            <Routes>
              <Route path="/" element={<MyAssignmentsMenu />} />
              <Route path="/:ilpID" element={<AssignmentCourseMap />} />
            </Routes>
          </Suspense>
        </AlpContext.Provider>
      )}
    </div>
  );
}
export const AlpContext = createContext(defaultState);
export default MyAssignmentsContainer;
